/**
 * FOTOS DE UN MUESTREO: «HACER FOTO» A UN LADO Y «SUBIR FOTO» AL OTRO, SIEMPRE
 * (23-09-2026, Pablo: «un lado para hacer foto, otro para subir foto; que puedas
 * subir las que quieras, un archivo junto a otro, y ya está»).
 *
 * Las dos mitades salen en cualquier aparato —también en el ordenador, donde
 * «Hacer foto» abre el selector de archivos porque `capture` no hace nada allí—:
 * un solo «Adjuntar foto» con ratón escondía la elección que Pablo quiere ver.
 *  · Hacer foto: `capture="environment"` (la cámara trasera, en el acto).
 *  · Subir foto: sin `capture`, `multiple`, y `.heic/.heif` a mano porque un
 *    iPhone no siempre rellena el tipo MIME y con `image/*` a secas deja las
 *    fotos en gris.
 * Sin tope de fotos: se suben todas, una detrás de otra.
 *
 * Dos detalles que ya costaron en otras pantallas:
 *  · el `.click()` cuelga del MISMO gesto (en otro tick Safari no abre nada);
 *  · `markFileCaptureStart()` antes de abrir: al volver de la cámara la app
 *    nativa (Despia) cree que se volvió de segundo plano y, sin esta marca, pinta
 *    «Bienvenido de nuevo» encima del diálogo (`lib/fileCaptureGate.ts`).
 */
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Camera, ImageUp, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { markFileCaptureStart } from "@/lib/fileCaptureGate";

export function PhotoSourceButtons({
  onFiles,
  testIdPrefix,
  className,
}: {
  onFiles: (files: File[]) => void;
  /** `manual` → `manual-photo-camera` / `manual-photo-gallery`. */
  testIdPrefix: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const galleryRef = useRef<HTMLInputElement | null>(null);

  const openCamera = () => {
    markFileCaptureStart();
    cameraRef.current?.click();
  };
  const openGallery = () => {
    markFileCaptureStart();
    galleryRef.current?.click();
  };
  const take = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length) onFiles(files);
  };

  const half = "flex min-w-0 items-center justify-center gap-2 rounded-xl border-2 px-2 py-3 text-[0.88rem] font-semibold transition-transform active:scale-[0.98]";
  return (
    <div className={className}>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={openCamera} data-testid={`${testIdPrefix}-photo-camera`} className={cn(half, "border-dashed border-teal-300 bg-teal-50/70 text-teal-900")}>
          <Camera className="h-5 w-5 shrink-0" />
          <span className="min-w-0 truncate">{t("common.capture.takePhoto")}</span>
        </button>
        <button type="button" onClick={openGallery} data-testid={`${testIdPrefix}-photo-gallery`} className={cn(half, "border-slate-200 bg-white text-slate-800")}>
          <ImageUp className="h-5 w-5 shrink-0 text-[#2E84DE]" />
          <span className="min-w-0 truncate">{t("healthMap.photo.upload")}</span>
        </button>
      </div>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={take} />
      <input ref={galleryRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden" onChange={take} />
    </div>
  );
}

/**
 * Las fotos ya elegidas, UNA JUNTO A OTRA en fila (y a la línea siguiente si no
 * caben), cada una con su X para quitarla.
 */
export function PhotoStrip({
  urls,
  onRemove,
  testId,
}: {
  urls: readonly string[];
  onRemove: (index: number) => void;
  testId?: string;
}) {
  const { t } = useTranslation();
  if (urls.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId} data-count={urls.length}>
      {urls.map((u, i) => (
        <div key={u} className="relative h-[4.5rem] w-[4.5rem] shrink-0">
          <img src={u} alt="" className="h-full w-full rounded-xl object-cover ring-1 ring-black/5" />
          <button
            type="button"
            onClick={() => onRemove(i)}
            className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-red-600 shadow ring-1 ring-black/10"
            aria-label={t("common.delete")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
